// src/publish/pieces/youtubePost.js — pieces of the YouTube video (format key 'youtube-post').
//
// The backend's YouTube content keeps `hook`, `body` and `cta` empty and puts
// everything in dedicated fields (the proposal's AS IS table): `description`
// is the only text that is pasted as is; the rest goes to other boxes of
// YouTube Studio or to people (speaker, designer). Pieces, in order, only when
// the field is non-empty:
//   description     primary, 'plain', LIMITS.youtubeDescription — the description
//                   paragraphs (a single newline stays a line break: the chapter
//                   list and the bullet-like lines are written that way) with the
//                   hashtags appended as one last "#a #b" paragraph
//   title           'plain', LIMITS.youtubeTitle
//   title_variants  'list' — one item per variant (a title may carry **bold**)
//   chapters        'list' — "<timestamp> <title>" per chapter, literal text
//   tags            'tags' — ONE paragraph "a, b, c": YouTube's tag field is
//                   comma-separated and takes no "#"
//   cta_script      'plain' — spoken on camera, not published
//   thumbnail       'plain' — thumbnail_concept, a brief for the designer
//   seo             'list' — "Primaria: …", "Secondaria: …", "Long tail: …"
//                   from seo_keywords (the prefixes are i18n: labels.youtube-post.seo*),
//                   literal text — a keyword is data, never parsed as markup
// `description_hook` is not a piece: the proposal's TO BE does not publish it
// (YouTube has no box for it and the description opens with its own paragraph).
// There is no link piece: the CTA URL already sits inside the description text.
// The primary is description, else title, else the first piece — exactly one.

import i18n from '../../i18n/index.js';
import { list, paragraph, text, textRuns } from '../blocks.js';
import { normalizeText, inlineRuns } from '../normalize.js';
import { makePiece, tagsBlocks, ensureOnePrimary, trimmed as str, arr, LIMITS } from '../piece.js';

const FORMAT = 'youtube-post';

const strings = (v) => arr(v).map(str).filter(Boolean);

/** Strings → one list block; `runsOf` reads each (inlineRuns or textRuns); [] when none. */
function listOf(items, runsOf) {
  const runs = items.map(runsOf).filter((r) => r.length);
  return runs.length ? [list(runs)] : [];
}

function chapterLines(chapters) {
  return arr(chapters)
    .filter((ch) => ch && typeof ch === 'object')
    .map((ch) => [str(ch.timestamp), str(ch.title)].filter(Boolean).join(' '))
    .filter(Boolean);
}

function seoLines(sk) {
  if (!sk || typeof sk !== 'object') return [];
  const label = (key) => i18n.t(`outputs:pieces.labels.${FORMAT}.${key}`);
  const out = [];
  const primary = str(sk.primary);
  if (primary) out.push(`${label('seoPrimary')}: ${primary}`);
  for (const k of strings(sk.secondary)) out.push(`${label('seoSecondary')}: ${k}`);
  for (const k of strings(sk.long_tail)) out.push(`${label('seoLongTail')}: ${k}`);
  return out;
}

export function build(entry) {
  const content = entry?.results?.content;
  if (!content || typeof content !== 'object') return [];
  const pieces = [];

  const description = [...normalizeText(str(content.description)), ...tagsBlocks(content.hashtags)];
  if (description.length) {
    pieces.push(makePiece(FORMAT, { id: 'description', blocks: description, limit: LIMITS.youtubeDescription }));
  }

  const title = normalizeText(str(content.title));
  if (title.length) pieces.push(makePiece(FORMAT, { id: 'title', blocks: title, limit: LIMITS.youtubeTitle }));

  const variants = listOf(strings(content.title_variants), inlineRuns);
  if (variants.length) pieces.push(makePiece(FORMAT, { id: 'title_variants', kind: 'list', blocks: variants }));

  const chapters = listOf(chapterLines(content.chapters), textRuns);
  if (chapters.length) pieces.push(makePiece(FORMAT, { id: 'chapters', kind: 'list', blocks: chapters }));

  const tags = strings(content.tags);
  if (tags.length) pieces.push(makePiece(FORMAT, { id: 'tags', kind: 'tags', blocks: [paragraph([text(tags.join(', '))])] }));

  const ctaScript = normalizeText(str(content.cta_script));
  if (ctaScript.length) pieces.push(makePiece(FORMAT, { id: 'cta_script', blocks: ctaScript }));

  const thumbnail = normalizeText(str(content.thumbnail_concept));
  if (thumbnail.length) pieces.push(makePiece(FORMAT, { id: 'thumbnail', blocks: thumbnail }));

  const seo = listOf(seoLines(content.seo_keywords), textRuns);
  if (seo.length) pieces.push(makePiece(FORMAT, { id: 'seo', kind: 'list', blocks: seo }));

  return ensureOnePrimary(pieces, ['description', 'title']);
}
